import type {
  BaseQueryFn,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import { toast } from 'sonner'
import type { AxiosError, AxiosRequestConfig } from 'axios'
import { retry, createApi } from '@reduxjs/toolkit/query/react'
import cloneDeep from 'lodash/cloneDeep'
import { type GiftSettingsType, type GiftCardInstanceType } from 'types/gifting'
import { isEqual } from 'lodash-es'
import type {
  ServiceCursorPaginatedResponseType,
  ServicePaginatedResponseType,
} from '@/connect-types/backend/service'
import type {
  Segment,
  SegmentPreviewTotalQueryType,
  SegmentType,
} from '@/state/entities/segment/segment.types'
import type { PaypalAccountType } from '@/pages/[org_id]/venues/[serial]/wi-fi/settings/paypal'
import type { PendingInvitationType } from '@/state/morpheus/types/invite/pending-invitation.types'
import type { InviteType } from '@/state/morpheus/types/invite/invite.types'
import type { OrganisationRegistrationReportType } from '@/state/entities/insight/insight.type'
import type { GalleryGetResponse } from '@/connect-types/members/galleryGetResponse.type'
import type { InteractionRequestType } from '@/state/entities/interactions/interactions.types'
import config from '@/config'
import { ServiceErrorHandler } from '@/connect-types/backend/service'
import type { OrganisationSettings } from '@/connect-types/organisations/organisations.type'
import type {
  PagenationRequestType,
  PaginationRepsonse,
} from '@/connect-types/pagination/pagination.type'
import type {
  CsvUploadRequestType,
  CsvUploadResponseType,
  CsvUploadsFromDynamo,
  SignedCsvUrlRequestType,
  SignedCsvUrlResponseType,
} from '@/connect-types/sources/csv.type'
import type { User } from '@/connect-types/user/user.type'
import type { OrganizationType } from '@/state/morpheus/types/organisation'
import type {
  OrganisationUsersAccess,
  UserRoleType,
} from '@/state/auth/auth.types'
import { setToken } from '@/utils/AuthTokenHelper'
import { customAxios, isAxiosError } from '@/utils/axiosHelper'
import { buildUrl } from '@/utils/common'
import { allSegment } from '@/state/entities/segment/segment.mock'
import { type CandidateProfileType } from '@/components/EndUserProfilePage/Candidate'
import { secondsEpoch } from '@/pages/[org_id]/gifting/reports'
import type { LoyaltyDataPaginationType } from '../entities/loyalty/loyalty.types'
import { shouldBailout } from '../helpers'
import { type MappedProfileItem } from '../entities/reports/reports.type'
import type {
  OrganizationRegistrationTransactionTotalsType,
  OrganizationRegistrationTransactionType,
} from './types/organization_registration/transactions.types'
import type { SegmentProfileType } from './types/segments/customers'
import type { SimpleVenueType, VenueSettingsType } from './types/venue'
import type {
  WebsiteProfileCookie,
  WebsiteType,
  WebstieEventType,
} from './types/websites/index.types'
import type { LokeIntegrationConfig } from './types/integreations/loke'
import type {
  InteractionType,
  InteractionProfileType,
} from './types/interaction'
import type {
  OrganisationType,
  SimpleOrganisationType,
} from './types/organisation'
import type {
  SimpleSegmentsMetadata,
  SimpleSegmentPreviewRequest,
  SimpleSegmentCountResponse,
  SimpleSegmentDataResponse,
  SimpleSegmentsListResponse,
  SimpleSegment,
  SimpleSegmentCreateRequest,
  SimpleSegmentUpdateRequest,
  SimpleSegmentDeleteResponse,
} from './types/simple-segments/index.types'
import { type UserConfigType } from './user-config/helpers'
import type {
  LocationPersonalisationCardsType,
  OrganisationPersonalisationCardType,
} from '@/state/entities/personalisation/personalisation.types'
import type { GiftReportResponse } from '@/connect-types/analytics/giftingAnalytics.type'
import type {
  GiftActivationMutation,
  GiftActivationType,
} from '@/connect-types/gifting/gifting.types'

// OAuth Credentials
export interface OAuthCredentials {
  accessToken: string
  expiresIn?: number
  refreshToken?: string
  scope?: string
  tokenType?: string
  expiresAt: number | null
}

// OAuth User Info
export interface OAuthUserInfo {
  sub: string
  email: string
  email_verified: boolean
  name: string
  given_name: string
  family_name: string
  created_at: number
  updated_at: number
  role: number
  two_factor_secret_set: boolean
  two_factor_enabled_at?: string
}

export interface SupportVerificationCode {
  code: string
  expires: string
}

export interface SupportVerificationCodeDetails {
  user: {
    uid: string
    email: string
    first: string | null
    last: string | null
  }
  generated_at: string
  expires_at: string
  ip_address: string
  user_agent: string | null
}

// Admin Organisation List Item - enriched search result
export interface AdminOrganisationListItem {
  id: string
  name: string
  parent_id?: string
  parent_organization_id?: string
  owner_id?: string
  stampede_owner_id?: string | null
  type?: 'reseller' | 'default' | 'root'
  website?: string | null
  created_at?: string
  is_live?: boolean
  chargebee_customer_id?: string | null
}

// Team Types
export interface OrgTeamMember {
  id?: string
  team_id: string
  user_id: string
  created_at?: string
  user?: {
    uid: string
    email: string
    first?: string
    last?: string
  }
}

// Result of `GET /organisations/:orgId/access`. Gate on `is_admin`; the other
// flags only explain why it is true and are mutually exclusive.
export interface OrganisationAdminAccess {
  is_admin: boolean
  is_direct_admin: boolean
  is_inherited_admin: boolean
  is_stampede_admin: boolean
}

// Users with direct access to an organisation (members + owner, deduped)
export interface OrganisationDirectUser {
  organization_id: string
  user_id: string
  role_id: string | null
  role: {
    id: string
    name: string
  } | null
  accessType: 'member' | 'owner'
  created_at: string
  oauth_users: User
}

export interface OrgTeam {
  id: string
  name: string
  organization_id: string
  owner_id?: string | null
  created_at: string
  updated_at: string
  deleted_at?: string | null
  owner?: {
    uid: string
    email: string
    first?: string
    last?: string
  }
  members?: OrgTeamMember[]
}

// Knowledge Base Types
export interface KnowledgeGuide {
  id: string
  title: string
  visibility: 'PUBLIC' | 'PRIVATE'
  author_oauth_user_id?: string | null | undefined
  tags?: string[]
  categories?: string[]
  created_at: string
  deleted_at: string | null | undefined
  blocks?: KnowledgeBlock[]
}

export interface KnowledgeBlock {
  id: string
  guide_id: string
  title: string
  description: string | null | undefined
  order: number
  created_at: string
  deleted_at: string | null | undefined
  images?: KnowledgeImage[]
}

export interface KnowledgeImage {
  id: string
  block_id: string
  url: string
  alt: string | null | undefined
  order: number
  created_at: string
  deleted_at: string | null | undefined
}

export interface KnowledgeBasePaginatedResponse<T> {
  data: T[]
  links: {
    first: string | null | undefined
    last: string | null | undefined
    next: string | null | undefined
    prev: string | null | undefined
  }
}

// Onboarding Types
export interface OnboardingJob {
  id: string
  organisation_id: string
  subscription_id: string | null
  status: 'pending' | 'processing' | 'completed' | 'failed'
  payload: OnboardingJobPayload
  created_by: string
  created_at: string
  updated_at: string
  completed_at: string | null
  error: string | null
  organisation: { id: string; name: string }
  created_by_user: {
    uid: string
    email: string
    first: string | null
    last: string | null
  }
  /**
   * The HubSpot deal this onboarding came from, captured when the job
   * is started. What the briefing panel beside the form is read from.
   */
  hubspot_deal_id?: string | null
}

export interface OnboardingJobLog {
  id: string
  onboarding_job_id: string
  step: string
  venue_identifier: string | null
  status: string
  message: string | null
  metadata: Record<string, unknown> | null
  created_at: string
}

export interface OnboardingJobVenueStep {
  id: string
  onboarding_job_venue_id: string
  step: string
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped'
  message: string | null
  metadata: Record<string, unknown> | null
  started_at: string | null
  completed_at: string | null
  created_at: string
}

export interface OnboardingJobVenue {
  id: string
  onboarding_job_id: string
  name: string
  serial: string | null
  products: string[]
  website_urls: string[]
  logo_url: string | null
  asset_urls: string[] | null
  status: 'pending' | 'processing' | 'completed' | 'failed'
  error: string | null
  created_at: string
  updated_at: string
  completed_at: string | null
  steps: OnboardingJobVenueStep[]
}

/** One thing the customer still has to do, as last polled. */
export interface SetupCheckResult {
  status: 'done' | 'outstanding' | 'unknown'
  detail: string
  first_passed_at?: string | null
  checked_at: string
}

export type SetupChecks = Record<string, SetupCheckResult>

/**
 * One check this job is subject to, switched off ones included. The
 * results only hold checks that ran, so this is what lets the panel show
 * a check that is off and offer it back.
 */
export interface SetupCheckSummary {
  id: string
  label: string
  disabled: boolean
}

/** What both check endpoints answer with: the results and what applies. */
export interface SetupChecksResponse {
  checks: SetupChecks
  catalogue: SetupCheckSummary[]
}

/**
 * Someone the setup reminder reaches, and whether this job has been told
 * to skip them. Muting is per job, not per account: the same person may
 * want chasing about one venue and not another.
 */
export interface ReminderRecipient {
  email: string
  name: string | null
  /** 'owner' for the account owner, otherwise their access role. */
  role: string
  muted: boolean
}

/**
 * One outstanding thing, as the CUSTOMER sees it.
 *
 * The same checks the admin panel polls, minus everything internal:
 * switched-off checks never appear, and a check that could not be
 * reached says so without the status code. See morpheus's
 * `organisations/:orgId/onboarding` controller for the full list of
 * what is deliberately left out.
 */
/**
 * One way of being shown how to do a step.
 *
 * Several per step, saying the same thing in different forms, because
 * the person doing the setup is sometimes at a desk and sometimes in
 * a kitchen with their hands full. They pick.
 */
export interface OnboardingStepGuide {
  kind: 'video' | 'written' | 'audio'
  title: string
  /**
   * A film in the knowledge base library, by slug. Preferred for
   * video: the library tracks coverage per second, knows whether this
   * person has watched it and where they stopped, and counts a view
   * and a completion against definitions written down once in trunk.
   */
  videoSlug?: string
  /** For anything the library does not hold. No watch tracking. */
  url?: string
  /** Running time for video and audio; reading time for written. */
  minutes?: number
}

export interface CustomerSetupCheck {
  /**
   * Where the work is done, as a route. Use this, not `href`: the
   * product navigates, and deriving a route from an absolute URL by
   * comparing origins works in production and fails everywhere else.
   */
  path?: string | null
  id: string
  label: string
  status: 'done' | 'outstanding' | 'unknown'
  detail: string
  /** What to call the link, and where it goes. Both from the check itself. */
  action: string | null
  href: string | null
  /** How to do it. Empty until content exists for this step. */
  guides?: OnboardingStepGuide[]
  first_passed_at: string | null
}

/**
 * Which face the portal wears, decided by morpheus's delivery model
 * (`portalVisibility`), never derived here.
 */
export type OnboardingPortalMode =
  | 'upcoming'
  | 'active'
  | 'waiting_on_you'
  | 'archived'
  | 'lapsed'
  | 'hidden'

/**
 * Null for the many organisations that never went through onboarding,
 * and for anyone the portal is not reachable by.
 */
/** One thing to do, from the plan. A prerequisite and a task read alike. */
export interface OnboardingPlanItem {
  id: string
  kind: 'task' | 'prerequisite'
  title: string
  /** Why it matters, in their terms. What stops it being argued with. */
  rationale: string | null
  /** Who it is on. Never both. */
  owner: 'you' | 'us'
  module: string
  moduleTitle: string
  venue: string
  /** Where it is done, relative to the organisation. */
  path: string | null
  /** How to do it. Empty until content exists for this step. */
  guides?: OnboardingStepGuide[]
  done: boolean
  dueAt: string | null
  overdue: boolean
}

export interface OnboardingPlanCheck {
  key: string
  title: string
  status: 'pending' | 'passed' | 'failed' | 'unknown'
  detail: string | null
  lastRunAt: string | null
}

export interface OnboardingPlanModule {
  module: string
  title: string
  status: string
  /** Waiting on something we have not shipped. Reads as ours. */
  deferred: boolean
  items: OnboardingPlanItem[]
  checks: OnboardingPlanCheck[]
}

export interface OnboardingPlanVenue {
  id: string
  name: string
  serial: string | null
  status: string
  pilot: boolean
  modules: OnboardingPlanModule[]
}

/**
 * The plan: per venue, per module, already grouped by who owns each
 * piece. Null for an organisation onboarded before engagements existed,
 * where the flat `checks` are all there is.
 */
export interface OnboardingPlan {
  engagementId: string
  status: string
  targetGoLive: string | null
  venues: OnboardingPlanVenue[]
  onYou: OnboardingPlanItem[]
  onUs: OnboardingPlanItem[]
  done: OnboardingPlanItem[]
  progress: { done: number; total: number }
}

/**
 * One onboarding call: booked, held, or still to arrange.
 *
 * `planned` ones are derived from what the account bought, so the list
 * of them is fixed. `ad_hoc` is the call the customer asked for, which
 * is why it carries a topic: it is the thing that turns "when are you
 * free" into a time somebody can prepare for.
 */
export interface OnboardingSession {
  key: string
  label: string
  durationMinutes: number
  state: 'held' | 'booked' | 'open'
  kind: 'planned' | 'ad_hoc'
  topic: string | null
  startsAt: string | null
  endsAt: string | null
  meetingUrl: string | null
  eventId: string | null
}

export interface OnboardingSessionsView {
  sessions: OnboardingSession[]
  specialist: { name: string | null; email: string } | null
}

/** When the venue can take a call. 0 = Sunday, as Date.getDay reports it. */
export interface OnboardingReachableWindow {
  day: number
  fromMinutes: number
  toMinutes: number
}

/**
 * One week of times for one call.
 *
 * The windows come back with the slots because the page draws a grid:
 * the free times alone cannot say whether an empty Thursday is a busy
 * diary or a day the venue is shut.
 */
export interface OnboardingAvailability {
  slots: string[]
  windows: OnboardingReachableWindow[]
  stepMinutes: number
  /** Nothing sooner than this, so the grid cannot offer a refused time. */
  minNoticeMinutes: number
  durationMinutes: number
  from: string
  specialist: { name: string | null; email: string } | null
  reason: string | null
}

export interface CustomerOnboardingView {
  /**
   * Does the sidebar carry this. Straight from the delivery model: the
   * schema is explicit that `onboarding_live` false hides the portal
   * whatever else is true, so the nav must not second-guess it.
   */
  nav: boolean
  mode: OnboardingPortalMode
  /** The real plan, when there is an engagement to read it from. */
  plan: OnboardingPlan | null
  checks: CustomerSetupCheck[]
  outstanding_count: number
  complete: boolean
  checks_run_at: string | null
  specialist: { name: string | null; email: string } | null
}

export interface OnboardingJobDetail extends OnboardingJob {
  venues: OnboardingJobVenue[]
  logs: OnboardingJobLog[]
  setup_checks?: SetupChecks | null
  checks_run_at?: string | null
  setup_check_catalogue?: SetupCheckSummary[]
  /**
   * Whether this customer is on Square. Not on the subscription, so
   * nothing in billing can answer it and somebody has to say.
   */
  uses_square?: boolean
  reminder_recipients?: ReminderRecipient[]
}

/**
 * A venue's real address, as resolved by the Google address lookup. Supply
 * one and the venue is created with it; omit it and the venue gets the
 * placeholder address that has to be corrected by hand later.
 */
export interface OnboardingVenueAddress {
  formattedAddress: string
  latitude?: number | null
  longitude?: number | null
  postCode?: string | null
  route?: string | null
  postalTown?: string | null
  administrativeAreaLevel1?: string | null
  administrativeAreaLevel2?: string | null
  country?: string | null
  googlePlaceId?: string | null
}

/** The roles an organisation member can be invited as, as morpheus names them. */
export type OnboardingTeamRole =
  | 'admin'
  | 'moderator'
  | 'gm'
  | 'reports'
  | 'marketeer'
  | 'booking'
  | 'front-of-house'
  | 'it-support'

/**
 * Someone to invite into the new account. `venues` indexes into the payload's
 * venues array rather than naming serials, because the venues do not exist
 * yet when this is built; an empty list means every venue.
 */
export interface OnboardingTeamMember {
  email: string
  role: OnboardingTeamRole
  venues: number[]
}

/** The setup-checklist email sent to everyone who gets an invite. */
export interface OnboardingWelcomeEmail {
  subject: string
  html: string
}

/** The brand kit a venue would be created with, before anything is created. */
export interface OnboardingBrandKitPreview {
  primaryColor: string
  interfaceColor: string
  headerColor: string
  footer: string
  background: string
  textColor: string
  /** The form-input fill. Part of the kit, so part of the preview. */
  input: string
  headerImage: string | null
  backgroundImage: string | null
  body_font_family: string
  heading_font_family: string
}

/** A Stampede person who has onboarding jobs, for the list's filter. */
export interface OnboardingOwner {
  uid: string
  email: string | null
  first: string | null
  last: string | null
  job_count: number
}

/** An event already in the admin's own calendar. */
export interface OnboardingCalendarEvent {
  id: string | null
  summary: string | null
  start: string | null
  end: string | null
  allDay: boolean
  attendees: string[]
  htmlLink: string | null
  meetingUrl: string | null
}

/** The first onboarding call, as picked in the wizard. */
export interface OnboardingMeeting {
  /**
   * What the session is for: "Bookings", "Loyalty", "Kick-off". An
   * onboarding runs a call per workstream, so the label is what tells
   * them apart in the calendar, the log and the welcome email.
   */
  label: string
  start: string
  end: string
  timeZone: string
  attendees: string[]
  summary?: string
  addMeet: boolean
}

/** A venue found on a group's own website, offered as a suggestion. */
export interface DiscoveredVenue {
  name: string
  address?: string
  website_url?: string
}

/** A generated email, rendered for review before the job runs. */
export interface OnboardingEmailPreview {
  context: string
  template_name: string
  email_html: string
}

export interface OnboardingJobPayload {
  /** The draft being completed, so the run finishes it rather than duplicating it. */
  job_id?: string
  organisation_id: string
  subscription_id?: string
  team?: OnboardingTeamMember[]
  welcome_email?: OnboardingWelcomeEmail
  /** Every session to book. One was never the right number. */
  onboarding_meetings?: OnboardingMeeting[]
  /** The single meeting drafts were saved with before. */
  onboarding_meeting?: OnboardingMeeting
  venues: {
    name: string
    website_urls: string[]
    products: string[]
    logo_url?: string
    asset_urls?: string[]
    address?: OnboardingVenueAddress
    /** Brand kit colours chosen by hand; they beat the derived ones. */
    brand_kit?: Record<string, string>
  }[]
}

export interface OnboardingJobListResponse {
  data: OnboardingJob[]
  meta: {
    page: number
    limit: number
    total: number
    has_more: boolean
  }
}

export interface OnboardingSubscriptionResponse {
  subscription: {
    organisation_id: string
    is_trial: boolean
    is_annual: boolean
    provider_id: string
    provider_type: string
    addons: Record<
      string,
      {
        code: string
        name: string
        quantity: number
        venues: string[]
      }
    >
  }
  features: Record<string, boolean>
  total_contacts: number
  active: boolean
}

const parseRoot = (value: Partial<Segment>) => {
  const sendNode: any = cloneDeep(value)
  if (!sendNode.segment.root) {
    return sendNode
  }
  if (
    Array.isArray(sendNode.segment.root.nodes) &&
    sendNode.segment.root.nodes.length === 1
  ) {
    sendNode.segment.root = sendNode.segment.root.nodes[0]
  }

  if (
    Array.isArray(sendNode.segment.root.nodes) &&
    sendNode.segment.root.nodes.length === 0
  ) {
    sendNode.segment.root = null
  }
  return sendNode
}

const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' }
  ): BaseQueryFn<{
    url: string
    method: AxiosRequestConfig['method']
    data?: AxiosRequestConfig['data']
    params?: AxiosRequestConfig['params']
    headers?: Record<string, string>
  }> =>
  async ({ url, method, data, params, headers }) => {
    try {
      let axiosUrl = baseUrl + url
      if (url.startsWith('https://')) {
        axiosUrl = url
      }

      const result = await customAxios({
        url: axiosUrl,
        method,
        data,
        params,
        headers,
      })

      return { data: result.data }
    } catch (axiosError) {
      const err = axiosError as AxiosError

      if (shouldBailout(err)) {
        retry.fail(err)
      }

      if (
        method !== 'get' &&
        err.response?.status !== 401 &&
        err.response?.status !== 404 &&
        !url.includes('segment') &&
        !url.includes('pin')
      ) {
        ServiceErrorHandler(err)
      }

      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      }
    }
  }

const morpheusApi = createApi({
  reducerPath: 'morpheus',
  keepUnusedDataFor: 60 * 60 * 24,
  baseQuery: retry(axiosBaseQuery({ baseUrl: config.url.morpheus }), {
    maxRetries: 1,
  }),

  refetchOnReconnect: true,
  tagTypes: [
    'Subscription',
    'Organisation',
    'User',
    'OrganisationSettings',
    'Venue',
    'Websites',
    'MarketingMenus',
    'Csv',
    'Segments',
    'CustomQuestions',
    'CustomQuestionEntities',
    'Forms',
    'Brand',
    'PayPal',
    'UserVenues',
    'Gifting',
    'GiftCardsActivations',
    'UserConfig',
    'Interactions',
    'PersonalisationCards',
    'ProfileTags',
    'KnowledgeBase',
    'VenuesSummary',
    'SimpleSegments',
    'SimpleSegmentsMetadata',
    'OnboardingJob',
    'CustomerOnboarding',
    'OrgTeam',
    'PendingInvitations',
    'Invites',
    'Gallery',
  ],
  endpoints: (build) => ({
    /**
     * Contact counts for an org over a window.
     *
     * `users` is the whole database, `opt_in_email_users` the part you may
     * actually email. Both are what "what is a contact worth" divides by, and
     * they live nowhere on the reporting summary.
     *
     * The report-builder reaches this through `useCustomerTotals`, which is
     * coupled to that page's redux date state and its `useFilters()` provider.
     * Neither exists elsewhere, hence a plain query taking the window as
     * arguments.
     */
    getCustomerTotals: build.query<
      OrganisationRegistrationReportType,
      { orgId: string; start: string; end: string; serials?: string[] }
    >({
      query: ({ orgId, start, end, serials }) => ({
        url: buildUrl(`/organisations/${orgId}/crm/customers/totals`, {
          start,
          end,
          ...(serials?.length ? { serial: serials.join(',') } : {}),
        }),
        method: 'GET',
      }),
    }),
    /**
     * Segments start
     */
    createSingleOrganisation: build.mutation<
      OrganizationType,
      {
        data: {
          name: string
          website: string
          company_size?: string
          job_title?: string
          venues?: { name: string; url?: string }[]
          referral_org_id?: string
          referral_code?: string
        }
      }
    >({
      query: ({ data }) => ({
        url: `/organisations`,
        method: 'POST',
        data,
      }),
    }),

    invite: build.mutation<{ id: string }, { orgId: string; data: object }>({
      query: ({ orgId, data }) => ({
        url: `/organisations/${orgId}/users/invite`,
        method: 'POST',
        data,
      }),
      // Sending an invite should refresh the team page's pending-invites
      // list immediately, without a manual refetch.
      invalidatesTags: [{ type: 'Invites', id: 'LIST' }],
    }),
    getInvites: build.query<
      ServicePaginatedResponseType<InviteType>,
      {
        orgId: string
      }
    >({
      query: ({ orgId }) => {
        return {
          url: `/organisations/${orgId}/users/invite?limit=100`,
          method: 'GET',
        }
      },
      providesTags: [{ type: 'Invites', id: 'LIST' }],
    }),
    deleteInvite: build.mutation<
      InviteType,
      {
        orgId: string
        id: string
      }
    >({
      query: ({ orgId, id }) => {
        return {
          url: `/organisations/${orgId}/users/invite/${id}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: [{ type: 'Invites', id: 'LIST' }],
    }),
    resendInvite: build.mutation<
      InviteType,
      {
        orgId: string
        id: string
      }
    >({
      query: ({ orgId, id }) => {
        return {
          url: `/organisations/${orgId}/users/invite/${id}/resend`,
          method: 'POST',
        }
      },
      invalidatesTags: [{ type: 'Invites', id: 'LIST' }],
    }),
    // The current user's own pending invitations to join organisations —
    // distinct from `invite` above, which sends an invite to someone else.
    getPendingInvitations: build.query<PendingInvitationType[], void>({
      query: () => ({
        url: `/invitations`,
        method: 'GET',
      }),
      providesTags: [{ type: 'PendingInvitations', id: 'LIST' }],
    }),
    respondToInvitation: build.mutation<
      { id: string; status: 'completed' | 'declined' },
      { id: string; action: 'accept' | 'delete' }
    >({
      query: ({ id, action }) => ({
        url: `/invitations/${id}`,
        method: 'POST',
        data: { action },
      }),
      invalidatesTags: [{ type: 'PendingInvitations', id: 'LIST' }],
    }),
    getReach: build.query<
      { all: number; email: number; sms: number },
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/crm/segments/all/data/reach`,
      }),
    }),
    getSegments: build.query<Segment[], { orgId: string }>({
      query: ({ orgId }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/crm/segments`,
      }),
      providesTags: (items: any) => [
        { type: 'Segments', id: 'LIST' },
        ...(items ?? []).map((item: any) => ({
          type: 'Segments' as const,
          id: item?.id,
        })),
      ],
    }),
    getSegment: build.query<Segment, { orgId: string; id: string }>({
      query: ({ orgId, id }) => ({
        url: `/organisations/${orgId}/crm/segments/${id}`,
        method: 'GET',
      }),
      providesTags: (data: any) =>
        data?.id ? [{ type: 'Segments', id: data.id }] : [],
    }),
    getSegmentPreview: build.query<
      ServicePaginatedResponseType<SegmentProfileType>,
      {
        orgId: string
        query: SegmentPreviewTotalQueryType
        cursor: { limit: number; cursor: string }
      }
    >({
      query: ({ orgId, query, cursor }) => ({
        url: buildUrl(`/organisations/${orgId}/crm/segments/preview`, cursor),
        method: 'POST',
        data: query,
      }),
    }),
    getSegmentPreviewAi: build.query<
      | (Segment['segment'] & {
          reasoning?: string
          explanation?: {
            intent: string
            data_used: string[]
            key_translations: { term: string; mapped_to: string }[]
            assumptions: string[]
            alternatives: string[]
          }
          _meta?: {
            trace_id: string
            attempts?: number
            model?: string
            cache?: 'hit' | 'miss'
            reach?: {
              all: number
              email: number
              sms: number
              bounded?: boolean
              estimated?: boolean
              ms: number
              source: 'cache' | 'full' | 'bounded'
            }
          }
        })
      | {
          simple_summary: string
          detailed_summary: string
          suggested_actions: string[]
          doing_well: string[]
          doing_poorly: string[]
          predictions: {
            required_improvement_percentage: number
            prediction_accuracy_percentage: number
            potential_improvement_percentage: number
            is_positive: boolean
            chart: {
              month: string
              bookings: number
              footfall: number
              revenue: number
              email_sends: number
              return_customers: number
            }[]
          }
        },
      {
        orgId: string
        query: string
        category:
          | 'loyalty'
          | 'reviews'
          | 'segments'
          | 'customQuestions'
          | 'bookings'
          | 'giftCards'
          | 'marketing'
          | 'forms'
          | 'reports'
          | 'monthlySnapshot'
      }
    >({
      query: ({ orgId, query, category }) => ({
        url: `/organisations/${orgId}/crm/segments/preview/ai`,

        method: 'POST',
        data: { query, category },
      }),
    }),
    createSegment: build.mutation<
      Segment,
      {
        orgId: string
        segment: Partial<Segment>
      }
    >({
      query: ({ orgId, segment }) => {
        const sendNode: any = parseRoot(segment)
        if (!sendNode.segment.is_venue_segment) {
          sendNode.segment.serials = []
        }
        return {
          method: 'post',
          url: `/organisations/${orgId}/crm/segments`,
          data: sendNode,
        }
      },
      invalidatesTags: () => [{ type: 'Segments', id: 'LIST' }],
      transformResponse: (item: Segment) => {
        toast.success(`${item?.name} created`)
        return item
      },
    }),
    deleteSegment: build.mutation<Segment, Segment>({
      query: (segment: any) => ({
        method: 'DELETE',
        url: `/organisations/${segment.organization_id}/crm/segments/${segment.id}`,
      }),
      invalidatesTags: () => [{ type: 'Segments', id: 'LIST' }],
      transformResponse: (item: Segment) => {
        toast.success(`${item?.name} deleted`)
        return item
      },
    }),
    updateSegment: build.mutation<
      Segment,
      {
        orgId: string
        segment: Segment
      }
    >({
      query: ({ orgId, segment }) => {
        const sendNode: any = parseRoot(segment)
        if (!sendNode.segment.is_venue_segment) {
          sendNode.segment.serials = []
        }

        return {
          method: 'put',
          url: `/organisations/${orgId}/crm/segments/${segment.id}`,
          data: sendNode,
        }
      },
      invalidatesTags: (item: any) =>
        item?.id ? [{ type: 'Segments', id: item?.id }] : [],
      transformResponse: (item: Segment) => {
        toast.success(`${item?.name} updated`)
        return item
      },
    }),
    getSegmentPreviewReach: build.query<
      { all: number; sms: number; email: number },
      {
        orgId: string
        query: SegmentPreviewTotalQueryType
      }
    >({
      query: ({ orgId, query }) => ({
        url: `/organisations/${orgId}/crm/segments/preview/reach`,
        method: 'POST',
        data: query,
      }),
    }),
    getSegmentMetadata: build.query<
      SegmentType,
      {
        orgId: string
      }
    >({
      query: ({ orgId }) => ({
        url: `/organisations/${orgId}/crm/segments/metadata`,
        method: 'GET',
      }),
      transformResponse(res: SegmentType) {
        const fieldsArray = Object.entries(res.fields).map(
          ([_key, value]) => value
        )

        return { ...res, fieldsArray }
      },
    }),
    /**
     * Segments end
     */

    /**
     * Simple Segments.
     */
    getSimpleSegments: build.query<
      SimpleSegmentsListResponse,
      {
        orgId: string
        cursor?: string
        limit?: number
      }
    >({
      query: ({ orgId, cursor, limit }) => ({
        url: `/organisations/${orgId}/crm/simple-segments`,
        method: 'GET',
        params: { cursor, limit },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: 'SimpleSegments' as const,
                id,
              })),
              { type: 'SimpleSegments', id: 'LIST' },
            ]
          : [{ type: 'SimpleSegments', id: 'LIST' }],
    }),
    getSimpleSegmentsMetadata: build.query<
      SimpleSegmentsMetadata,
      {
        orgId: string
      }
    >({
      query: ({ orgId }) => ({
        url: `/organisations/${orgId}/crm/simple-segments/metadata`,
        method: 'GET',
      }),
      providesTags: ['SimpleSegmentsMetadata'],
    }),
    getSimpleSegmentsPreviewCount: build.mutation<
      SimpleSegmentCountResponse,
      {
        orgId: string
        data: SimpleSegmentPreviewRequest
      }
    >({
      query: ({ orgId, data }) => ({
        url: `/organisations/${orgId}/crm/simple-segments/count`,
        method: 'POST',
        data,
      }),
    }),
    getSimpleSegmentsPreviewData: build.mutation<
      SimpleSegmentDataResponse,
      {
        orgId: string
        data: SimpleSegmentPreviewRequest
      }
    >({
      query: ({ orgId, data }) => ({
        url: `/organisations/${orgId}/crm/simple-segments/data`,
        method: 'POST',
        data,
      }),
    }),
    getSimpleSegment: build.query<
      SimpleSegment,
      {
        orgId: string
        id: string
      }
    >({
      query: ({ orgId, id }) => ({
        url: `/organisations/${orgId}/crm/simple-segments/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [{ type: 'SimpleSegments', id }],
    }),
    getSimpleSegmentCount: build.query<
      SimpleSegmentCountResponse,
      {
        orgId: string
        id: string
        mode?: 'email' | 'sms' | 'all'
      }
    >({
      query: ({ orgId, id, mode }) => ({
        url: `/organisations/${orgId}/crm/simple-segments/${id}/count`,
        method: 'GET',
        params: mode ? { mode } : undefined,
      }),
      providesTags: (result, error, { id }) => [{ type: 'SimpleSegments', id }],
    }),
    getSimpleSegmentData: build.query<
      SimpleSegmentDataResponse,
      {
        orgId: string
        id: string
        cursor?: string
        limit?: number
      }
    >({
      query: ({ orgId, id, cursor, limit }) => ({
        url: `/organisations/${orgId}/crm/simple-segments/${id}/data`,
        method: 'GET',
        params: { cursor, limit },
      }),
      providesTags: (result, error, { id }) => [{ type: 'SimpleSegments', id }],
    }),
    createSimpleSegment: build.mutation<
      SimpleSegment,
      {
        orgId: string
        data: SimpleSegmentCreateRequest
      }
    >({
      query: ({ orgId, data }) => ({
        url: `/organisations/${orgId}/crm/simple-segments`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'SimpleSegments', id: 'LIST' }],
    }),
    updateSimpleSegment: build.mutation<
      SimpleSegment,
      {
        orgId: string
        id: string
        data: SimpleSegmentUpdateRequest
      }
    >({
      query: ({ orgId, id, data }) => ({
        url: `/organisations/${orgId}/crm/simple-segments/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'SimpleSegments', id },
        { type: 'SimpleSegments', id: 'LIST' },
      ],
    }),
    deleteSimpleSegment: build.mutation<
      SimpleSegmentDeleteResponse,
      {
        orgId: string
        id: string
      }
    >({
      query: ({ orgId, id }) => ({
        url: `/organisations/${orgId}/crm/simple-segments/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'SimpleSegments', id },
        { type: 'SimpleSegments', id: 'LIST' },
      ],
    }),

    getOrganisationsSearch: build.query<
      {
        data: AdminOrganisationListItem[]
        links: {
          first: number | null
          last: number | null
          next: number | null
          prev: number | null
          count: number
        }
      },
      {
        limit: number
        offset?: number
        search?: string
        team_id?: string
        is_live?: boolean
      }
    >({
      query: ({ limit, offset, search, team_id, is_live }) => ({
        url: buildUrl(`/organisations/search`, {
          limit,
          offset,
          q: search,
          team_id,
          is_live,
        }),
        method: 'get',
      }),
      providesTags: (result, error, { is_live }) =>
        result?.data
          ? [
              ...result.data.map((org) => ({
                type: 'Organisation' as const,
                id: org.id,
              })),
              {
                type: 'Organisation' as const,
                id: `SEARCH_${is_live ?? 'all'}`,
              },
            ]
          : [{ type: 'Organisation', id: `SEARCH_${is_live ?? 'all'}` }],
    }),
    getOrganisations: build.query<
      ServiceCursorPaginatedResponseType<SimpleOrganisationType>,
      { cursor: string; limit: number; search?: string }
    >({
      query: ({ limit, search, cursor }) => ({
        url: buildUrl(`/organisations`, {
          cursor,
          limit,
          search,
          is_live: true,
        }),
        method: 'get',
      }),
    }),
    getLocations: build.query<
      ServiceCursorPaginatedResponseType<SimpleVenueType>,
      { cursor: string; limit: number; search?: string }
    >({
      query: ({ limit, search, cursor }) => ({
        url: buildUrl(`/venues`, { cursor, limit, search }),
        method: 'get',
      }),
    }),
    /**
     *  organisations
     */
    getOrganisation: build.query<OrganisationType, string>({
      //  keepUnusedDataFor: 600,
      query: (orgId: any) => ({
        url: `/organisations/${orgId}`,
        method: 'get',
      }),
      transformResponse(data: OrganisationType) {
        data.children = []

        return data
      },
      providesTags: (result: any) => [{ type: 'Organisation', id: result?.id }],
    }),
    updateOrganisation: build.mutation<
      OrganisationType,
      {
        orgId: string
        data: {
          website?: string
          name?: string
          timezone?: string | null
          is_live?: boolean
          owner_id?: string
          stampede_owner_id?: string | null
        }
      }
    >({
      query: ({ orgId, data }) => ({
        url: `/organisations/${orgId}`,
        method: 'put',
        data,
      }),

      invalidatesTags: (result: any) => [
        { type: 'Organisation', id: result?.id },
        { type: 'Organisation', id: 'SEARCH' },
        { type: 'Organisation', id: 'LIST' },
      ],
    }),
    getOrganisationSettings: build.query<OrganisationSettings, string>({
      query: (orgId: any) => ({
        url: `/organisations/${orgId}/settings`,
        method: 'get',
      }),
      providesTags: ['OrganisationSettings'],
    }),
    updateOrganisationSettings: build.mutation<
      OrganisationSettings,
      { orgId: string; data: OrganisationSettings['settings'] }
    >({
      query: ({ orgId, data }) => ({
        url: `/organisations/${orgId}/settings`,
        method: 'put',
        data,
      }),
      async onQueryStarted({ orgId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            morpheusApi.util.updateQueryData(
              'getOrganisationSettings',
              orgId,
              () => data
            )
          )
          toast.success('Settings updated')
        } catch {
          toast.error('Something went wrong, could not update settings')
        }
      },
    }),
    getOrganisationChildren: build.query<
      {
        data: OrganisationType[]
        meta: { total: number; limit: number; offset: number }
      },
      {
        orgId: string
        limit?: number
        offset?: number
        search?: string
      }
    >({
      query: ({ orgId, limit = 50, offset = 0, search }) => ({
        url: buildUrl(`/organisations/${orgId}/children`, {
          limit,
          offset,
          search,
          is_live: true,
        }),
        method: 'get',
      }),
      transformResponse(response: any) {
        // Backwards-compat: older backend returns a plain array.
        if (Array.isArray(response)) {
          return {
            data: response.map((item: any) => ({
              ...item,
              children: item?.other_organization,
            })),
            meta: {
              total: response.length,
              limit: response.length,
              offset: 0,
            },
          }
        }
        return {
          data: (response?.data ?? []).map((item: any) => ({
            ...item,
            children: item?.other_organization,
          })),
          meta: response?.meta ?? {
            total: response?.data?.length ?? 0,
            limit: 50,
            offset: 0,
          },
        }
      },
      providesTags: (result: any) => {
        if (!result?.data) return [{ type: 'Organisation', id: 'LIST' }]
        return [
          ...result.data.map((item: any) => ({
            type: 'Organisation' as const,
            id: item?.id,
          })),
          { type: 'Organisation', id: 'LIST' },
        ]
      },
    }),
    updateOrganisationLocations: build.mutation<
      OrganisationType,
      { orgId: string; serials: string[]; originalOrgId: string }
    >({
      query: ({ orgId, serials, originalOrgId }) => ({
        url: `/organisations/${orgId}/children`,
        method: 'put',
        data: { serials, originalOrgId },
      }),
      transformResponse(data: OrganisationType) {
        toast.success(`Venue(s) moved`)
        return data
      },
      invalidatesTags: () => [{ type: 'Organisation', id: 'LIST' }],
    }),
    createOrganisation: build.mutation<
      OrganisationType,
      {
        orgId: string
        data: { name: string; owner_id?: string; website?: string }
      }
    >({
      query: ({ orgId, data }) => ({
        url: `/organisations/${orgId}/children`,
        method: 'POST',
        data,
      }),

      invalidatesTags: [{ type: 'Organisation', id: 'LIST' }],
    }),

    /**
     * End organisations
     */

    getUser: build.query<User, string>({
      //  keepUnusedDataFor: 0,

      query: (_uid: any) => ({
        url: `/members`,
        method: 'get',
      }),

      providesTags: (_uid: any) => [
        { type: 'User' as const, id: _uid?.uid },
        { type: 'User', id: 'LIST' },
      ],
    }),
    updateUser: build.mutation<User, Partial<User>>({
      query: (data: any) => ({
        url: `/members/me`,
        method: 'put',
        data,
      }),
      invalidatesTags: (data: any) => [
        { type: 'User' as const, id: data?.uid ?? '' },
        { type: 'User', id: 'LIST' },
      ],
      transformResponse({
        message,
        updated_user,
      }: {
        message: string
        updated_user: User
      }) {
        toast.success(message)
        return updated_user
      },
    }),
    getUserConfig: build.query<UserConfigType, null>({
      // keepUnusedDataFor: 0,
      query: () => ({
        url: `/members/config`,
        method: 'get',
      }),

      providesTags: [{ type: 'UserConfig', id: 'LIST' }],
    }),
    updateUserConfig: build.mutation<UserConfigType, Partial<UserConfigType>>({
      query: (data: any) => ({
        url: `/members/config`,
        method: 'put',
        data,
      }),
      invalidatesTags: [{ type: 'UserConfig', id: 'LIST' }],
    }),
    getAuthCodeUsingOrganisationPinV2: build.mutation<
      string,
      { orgId: string; oauthUserId: string; pin: string; state: string }
    >({
      query: ({ orgId, oauthUserId, pin, state }) => ({
        url: `/organisations/${orgId}/users/pin/v2`,
        method: 'GET',
        params: {
          oauth_user_id: oauthUserId,
          pin,
          state,
          client_id: config.auth.clientId,
          redirect_uri: config.auth.redirectUri,
        },
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),
    deleteOrganisationUserPin: build.mutation<
      UserRoleType,
      { orgId: string; uid: string }
    >({
      query: ({ orgId, uid }) => ({
        url: `/organisations/${orgId}/users/${uid}/pin`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),
    getOrganisationUserPin: build.query<
      { has_pin: boolean; created_at?: string },
      { orgId: string; uid: string }
    >({
      query: ({ orgId, uid }) => ({
        url: `/organisations/${orgId}/users/${uid}/pin`,
        method: 'GET',
      }),
      providesTags: [{ type: 'User', id: 'LIST' }],
    }),
    updateOrganisationUserPin: build.mutation<
      { has_pin: boolean; created_at?: string },
      { orgId: string; uid: string; pin: string }
    >({
      query: ({ orgId, uid, pin }) => ({
        url: `/organisations/${orgId}/users/${uid}/pin`,
        method: 'PUT',
        data: { pin },
      }),
      invalidatesTags: (_, _1, { uid }) => [
        { type: 'User', id: uid },
        { type: 'User', id: 'LIST' },
      ],
    }),

    getOrganisationAccess: build.query<
      OrganisationAdminAccess,
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: `/organisations/${orgId}/access`,
        method: 'GET',
      }),
    }),
    getOrganisationUserRole: build.query<
      UserRoleType,
      { orgId: string; uid: string }
    >({
      query: ({ orgId, uid }) => ({
        url: `/organisations/${orgId}/users/${uid}/role`,
        method: 'get',
      }),
      providesTags: [{ type: 'User', id: 'LIST' }],
    }),
    getOrganisationUserRoles: build.query<UserRoleType[], { orgId: string }>({
      query: ({ orgId }) => ({
        url: `/organisations/${orgId}/users/roles`,
        method: 'GET',
      }),
    }),
    updateOrganisationUserRole: build.mutation<
      UserRoleType,
      { orgId: string; uid: string; role: string }
    >({
      query: ({ orgId, uid, role }) => ({
        url: `/organisations/${orgId}/users/${uid}/role`,
        method: 'PUT',
        data: { role },
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),
    removeOrganisationUser: build.mutation<
      UserRoleType,
      { orgId: string; uid: string; role: string }
    >({
      query: ({ orgId, uid }) => ({
        url: `/organisations/${orgId}/users/${uid}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),
    getOrganisationUser: build.query<User, { orgId: string; uid: string }>({
      query: ({ orgId, uid }) => ({
        url: `/organisations/${orgId}/users/${uid}`,
        method: 'get',
      }),
    }),
    getOrganisationUsers: build.query<
      OrganisationUsersAccess[],
      {
        orgId: string
        serial?: string
        filterType?: (
          | 'all'
          | 'location-bound'
          | 'direct'
          | 'recursive'
          | 'owner'
        )[]
        filterRoles?: ('it-support' | 'admin')[]
      }
    >({
      query: ({ orgId, filterType, serial, filterRoles }) => ({
        url: `/organisations/${orgId}/users`,
        method: 'get',
        params: {
          ...(filterType ? { filterType } : {}),
          ...(serial ? { serials: [serial] } : {}),
          ...(filterRoles ? { filterRoles } : {}),
        },
      }),
      providesTags: [{ type: 'User', id: 'LIST' }],
    }),
    getOrganisationDirectUsers: build.query<
      OrganisationDirectUser[],
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: `/organisations/${orgId}/direct-users`,
        method: 'get',
      }),
      providesTags: [{ type: 'User', id: 'LIST' }],
    }),
    getOrganisationUserVenues: build.query<
      unknown[],
      { orgId: string; uid: string }
    >({
      query: ({ orgId, uid }) => ({
        url: `/organisations/${orgId}/users/${uid}/venues`,
        method: 'get',
      }),
      providesTags: [{ type: 'UserVenues', id: 'LIST' }],
    }),
    updateOrganisationUserVenues: build.mutation<
      VenueSettingsType[],
      { orgId: string; uid: string; access: string[] }
    >({
      query: ({ orgId, uid, access }) => ({
        url: `/organisations/${orgId}/users/${uid}/venues`,
        method: 'put',
        data: { access },
      }),
      invalidatesTags: [
        { type: 'UserVenues', id: 'LIST' },
        {
          type: 'User',
          id: 'LIST',
        },
      ],
    }),
    getHubspotAuthToken: build.query<{ token: string }, unknown>({
      query: () => ({
        url: `/oauth/hubspot-visitor-identification`,
        method: 'get',
      }),
    }),
    getZendeskToken: build.query<{ token: string }, unknown>({
      query: () => ({
        url: `/oauth/zendesk-identification`,
        method: 'get',
      }),
    }),
    updateOauthUserPassword: build.mutation<
      { message: string },
      { password: string }
    >({
      query: (input: any) => ({
        url: `/oauth/update-password`,
        method: 'PUT',
        data: { password: input.password },
      }),
    }),

    getAuthWithCode: build.query<OAuthCredentials, { code: string }>({
      query: ({ code }) => ({
        url: `/oauth/token`,
        method: 'post',
        data: {
          code,
          client_id: config.auth.clientId,
          grant_type: 'authorization_code',
          redirect_uri: config.auth.redirectUri,
        },
      }),
      transformResponse(response: {
        access_token: string
        expires_in?: number
        refresh_token?: string
        scope?: string
        token_type?: string
      }) {
        setToken({
          accessToken: response.access_token,
          refreshToken: response.refresh_token,
          scope: response.scope,
          tokenType: response.token_type,
          expiresIn: response.expires_in,
          expiresAt: response.expires_in
            ? Date.now() + response.expires_in * 1000
            : null,
        })

        return {
          accessToken: response.access_token,
          expiresIn: response.expires_in,
          refreshToken: response.refresh_token,
          scope: response.scope,
          tokenType: response.token_type,
          expiresAt: response.expires_in
            ? Date.now() + response.expires_in * 1000
            : null,
        }
      },
    }),

    /**
     * Venue
     */
    getOperatingTypes: build.query<
      { id: string; name: string; sic_code: string }[],
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: `/organisations/${orgId}/venues/vertical-types`,
        method: 'get',
      }),
    }),
    getVenues: build.query<
      ServicePaginatedResponseType<SimpleVenueType>,
      { orgId: string; show_child_organisations: boolean }
    >({
      query: ({ orgId, show_child_organisations: lookChildren }) => ({
        url: buildUrl(`/organisations/${orgId}/venues`, {
          lookChildren,
          limit: 100,
        }),
        method: 'get',
      }),
    }),
    addVenue: build.mutation<
      VenueSettingsType,
      { orgId: string; data?: { name: string } }
    >({
      query: ({ orgId, data }) => ({
        url: `/organisations/${orgId}/venues`,
        method: 'post',
        data,
      }),
      invalidatesTags: (_, _1, { orgId }) => [
        { type: 'Organisation', id: orgId },
      ],
      transformResponse(data: VenueSettingsType) {
        return data
      },
    }),
    updateBrandLocation: build.mutation<
      unknown,
      { orgId: string; serial: string; branding_id: string }
    >({
      query: ({ serial, orgId, branding_id }) => ({
        url: `/organisations/${orgId}/venues/${serial}/brand`,
        method: 'PUT',
        data: {
          branding_id,
        },
      }),
      invalidatesTags: (_, _1, { serial, branding_id }) => [
        { type: 'Venue', id: serial },
        { type: 'Brand', id: branding_id },
      ],
      transformResponse(data) {
        return data
      },
    }),
    getLocation: build.query<
      VenueSettingsType,
      { orgId: string; serial: string }
    >({
      query: ({ serial, orgId }) => ({
        url: `/organisations/${orgId}/venues/${serial}`,
        method: 'GET',
      }),
      transformResponse(data: VenueSettingsType) {
        if (!data.paymentType) {
          data.paymentType = ''
        }
        return data
      },
      providesTags: (data, _error, { serial, orgId }) => {
        if (!data) return []
        const { custom_question_entries, id } = data

        return [
          { type: 'Organisation', id: orgId },
          { type: 'Venue', id: serial },
          { type: 'Venue', id },
          ...custom_question_entries.map((item: any) => ({
            type: 'CustomQuestionEntities' as const,
            id: item?.id,
          })),
          ...custom_question_entries.map((item: any) => ({
            type: 'CustomQuestions' as const,
            id: item?.custom_question_id,
          })),
        ]
      },
    }),
    updateLocation: build.mutation<
      VenueSettingsType,
      { orgId: string; serial: string; venue: Partial<VenueSettingsType> }
    >({
      query: ({ serial, orgId, venue }) => ({
        url: `/organisations/${orgId}/venues/${serial}`,
        method: 'PUT',
        data: venue,
      }),
      transformResponse(data: VenueSettingsType) {
        toast.success(`${data.alias} updated`)
        return data
      },
      invalidatesTags: (data, _error, { serial, orgId }) => [
        { type: 'Organisation', id: orgId },
        { type: 'Venue', id: serial },
        { type: 'Brand', id: data?.branding_settings?.id },
      ],
    }),
    /**
     * End venue
     */

    /**
     * Reports
     */
    getTransactions: build.query<
      ServiceCursorPaginatedResponseType<OrganizationRegistrationTransactionType>,
      {
        orgId: string
        query: InteractionRequestType
      }
    >({
      query: ({ orgId, query }) => ({
        method: 'get',
        url: buildUrl(`/organisations/${orgId}/transactions`, query),
      }),
    }),
    getTransactionTotals: build.query<
      OrganizationRegistrationTransactionTotalsType,
      {
        orgId: string
        query: InteractionRequestType
      }
    >({
      query: ({ orgId, query }) => ({
        method: 'get',
        url: buildUrl(`/organisations/${orgId}/transactions/totals`, query),
      }),
    }),
    getInteractions: build.query<
      ServiceCursorPaginatedResponseType<InteractionType>,
      {
        orgId: string
        query: InteractionRequestType
      }
    >({
      query: ({ orgId, query }) => ({
        method: 'get',
        url: buildUrl(`/organisations/${orgId}/interactions`, {
          ...query,
          query_type: 'basic',
        }),
      }),
    }),

    getBatchRecentInteractions: build.query<
      Record<string, InteractionType[]>,
      {
        orgId: string
        daysBack?: number
        limitPerSerial?: number
      }
    >({
      query: ({ orgId, daysBack = 7, limitPerSerial = 5 }) => ({
        method: 'get',
        url: buildUrl(`/organisations/${orgId}/interactions/batch`, {
          days_back: daysBack,
          limit_per_serial: limitPerSerial,
        }),
      }),
    }),

    getOriginReport: build.query<
      MappedProfileItem[],
      { serial: string; start: Date; end: Date; orgId: string }
    >({
      query: ({ orgId, start, end, serial }) => ({
        url: buildUrl(`/organisations/${orgId}/crm/reports/origin`, {
          start: secondsEpoch(start.getTime()),
          end: secondsEpoch(end.getTime()),
          serial,
        }),
        method: 'get',
      }),
    }),
    /**
     * Website
     */
    getWebsites: build.query<
      WebsiteType[],
      {
        orgId: string
      }
    >({
      query: ({ orgId }) => ({
        method: 'get',
        url: `/organisations/${orgId}/website`,
      }),
      providesTags: [{ id: 'LIST', type: 'Websites' }],
    }),
    createWebsite: build.mutation<WebsiteType, { orgId: string; url: string }>({
      query: ({ orgId, url }) => ({
        url: `/organisations/${orgId}/website`,
        method: 'POST',
        data: { url },
      }),
      invalidatesTags: [{ type: 'Websites', id: 'LIST' }],
      transformResponse(data: WebsiteType) {
        toast.success(`${data.url} created`)
        return data
      },
    }),
    getWebsiteEvents: build.query<
      ServiceCursorPaginatedResponseType<WebstieEventType>,
      { cursor: string; limit: number; search?: string; orgId: string }
    >({
      query: ({ orgId, cursor, limit, search }) => ({
        method: 'get',
        url: buildUrl(`/organisations/${orgId}/website/events`, {
          search,
          limit,
          cursor,
        }),
      }),
    }),
    getWebsiteProfiles: build.query<
      ServiceCursorPaginatedResponseType<WebsiteProfileCookie>,
      { cursor: string; limit: number; search?: string; orgId: string }
    >({
      query: ({ orgId, cursor, limit, search }) => ({
        method: 'get',
        url: buildUrl(`/organisations/${orgId}/website/profiles`, {
          search,
          limit,
          cursor,
        }),
      }),
    }),
    createCsvSignedUrl: build.mutation<
      SignedCsvUrlResponseType,
      SignedCsvUrlRequestType
    >({
      query: ({
        orgId,
        serials,
        data_source,
        default_region,
        original_file_name = new Date().toTimeString(),
        tags,
      }) => ({
        method: 'post',
        url: buildUrl(`/organisations/${orgId}/csv/uploads`, {}),
        data: {
          serials,
          data_source,
          region: default_region,
          original_file_name,
          type: 'user',
          tags,
        },
      }),
    }),
    createCsvUpload: build.mutation<
      CsvUploadResponseType,
      CsvUploadRequestType
    >({
      query: ({ url, data }) => ({
        method: 'put',
        url,
        headers: {
          'Content-Type': 'text/csv',
        },
        timeout: 1000 * 60,
        data,
      }),
      transformResponse(data: CsvUploadResponseType) {
        toast.success('Csv uploaded for processing')
        return data
      },
      transformErrorResponse: (error: any) => {
        toast.warning('Something went wrong')
        return error
      },
    }),
    getCsvUploads: build.query<
      PaginationRepsonse<CsvUploadsFromDynamo>,
      { orgId: string; page: { offset: number; limit: number } }
    >({
      query: ({ orgId, page }) => ({
        method: 'get',
        url: buildUrl(`/organisations/${orgId}/csv/uploads`, page),
      }),
    }),
    /**
     * Loyalty start
     */

    getLoyaltySchemeUsers: build.query<
      LoyaltyDataPaginationType,
      {
        orgId: string
        schemeId: string
        cursor: PagenationRequestType
      }
    >({
      query: ({ orgId, schemeId, cursor }) => ({
        method: 'get',
        url: buildUrl(
          `/organisations/${orgId}/loyalty/${schemeId}/customers`,
          cursor
        ),
      }),
      // providesTags: (item: any) => [{ id: item?.id, type: 'LoyaltySchemes' }],
    }),

    /**
     * Loyalty end
     */

    /**
     * Stripe
     */

    getStripeAccounts: build.query<
      {
        account_display_name: string | null | undefined
        livemode: 0 | 1
        stripe_user_id: string
        scope: 'read_write'
        id: number
        token_type: 'bearer'
      }[],
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        method: 'get',
        url: `/organisations/${orgId}/integrations/stripe`,
      }),
    }),

    /**
     * Stripe end
     */

    getPayPalAccounts: build.query<
      { id: string; name: string }[],
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        method: 'get',
        url: `/organisations/${orgId}/integrations/paypal`,
      }),
      providesTags: [{ type: 'PayPal', id: 'LIST' }],
    }),
    updatePayPalAccounts: build.mutation<PaypalAccountType, { orgId: string }>({
      query: ({ orgId }) => ({
        method: 'PUT',
        url: `/organisations/${orgId}/integrations/paypal`,
      }),
    }),
    createPayPalAccounts: build.mutation<
      PaypalAccountType,
      {
        orgId: string
        data: Partial<PaypalAccountType>
      }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/integrations/paypal`,
        data,
      }),
      invalidatesTags: [{ type: 'PayPal', id: 'LIST' }],
    }),

    /**
     * Loke integration
     */
    getLokeConfig: build.query<LokeIntegrationConfig, { org_id: string }>({
      query: ({ org_id }) => ({
        url: `/organisations/${org_id}/integrations/loke`,
        method: 'get',
      }),
    }),
    modifyLokeConfig: build.mutation<
      LokeIntegrationConfig,
      {
        org_id: string
        data: LokeIntegrationConfig
      }
    >({
      query: ({ org_id, data }) => ({
        url: `/organisations/${org_id}/integrations/loke`,
        method: 'put',
        data,
      }),
      invalidatesTags: (res: any) => {
        return [{ id: res?.id, type: 'loke-config' }]
      },
    }),

    /**
     *
     *
     */

    /**
     * ----
     * TAGS START
     * ----
     */
    getTags: build.query<
      TagType[],
      {
        orgId: string
      }
    >({
      query: ({ orgId }) => ({
        url: `/organisations/${orgId}/crm/tags`,
        method: 'get',
      }),
      providesTags: (items: any) => [
        ...items.map((item: any) => ({
          id: item?.id,
          type: 'ProfileTags' as const,
        })),
        { id: 'LIST', type: 'ProfileTags' },
      ],
      transformResponse: (items: TagType[]) =>
        [...items].sort((a, b) => a.name.localeCompare(b.name)),
    }),
    deleteTag: build.mutation<
      boolean,
      {
        orgId: string
        tag: TagType
      }
    >({
      query: ({ orgId, tag }) => ({
        url: `/organisations/${orgId}/crm/tags/${tag.id}`,
        method: 'delete',
      }),
      invalidatesTags: [{ id: 'LIST', type: 'ProfileTags' }],
      transformResponse: (_1, _2, { tag }) => {
        toast.success(`${tag.name} deleted`)
        return true
      },
    }),
    updateTag: build.mutation<
      TagType,
      {
        orgId: string
        tag: TagType
      }
    >({
      query: ({ orgId, tag }) => ({
        url: `/organisations/${orgId}/crm/tags/${tag.id}`,
        method: 'put',
        data: {
          name: tag.name,
        },
      }),
      invalidatesTags: [{ id: 'LIST', type: 'ProfileTags' }],
      transformResponse: (item: TagType) => {
        toast.success(`${item?.name} updated`)
        return item
      },
    }),
    createTag: build.mutation<
      TagType,
      {
        orgId: string
        name: string
      }
    >({
      query: ({ orgId, name }) => ({
        url: `/organisations/${orgId}/crm/tags`,
        method: 'post',
        data: { name },
      }),
      invalidatesTags: [{ id: 'LIST', type: 'ProfileTags' }],
      transformResponse: (item: TagType) => {
        toast.success(`${item?.name} created`)
        return item
      },
    }),
    /**
     * ----
     * TAGS END
     * ----
     */

    /**
     * Gifting start
     */

    getGiftCards: build.query<
      GiftSettingsType[],
      {
        orgId: string
      }
    >({
      query: ({ orgId }) => ({
        url: `/organisations/${orgId}/gifting`,
        method: 'get',
      }),
      providesTags: (results: any) =>
        results ? [{ type: 'Gifting', id: 'LIST' }] : [],
    }),
    getGiftCard: build.query<
      GiftSettingsType,
      {
        orgId: string
        id: string
      }
    >({
      query: ({ orgId, id }) => ({
        url: `/organisations/${orgId}/gifting/schemes/${id}`,
        method: 'get',
      }),
      providesTags: (results: any) => [{ type: 'Gifting', id: results?.id }],
    }),

    createGiftCard: build.mutation<
      GiftSettingsType,
      {
        orgId: string
        data: Partial<GiftSettingsType>
      }
    >({
      query: ({ orgId, data }) => ({
        url: `/organisations/${orgId}/gifting`,
        method: 'post',
        data: { ...data, quantities: undefined },
      }),
      invalidatesTags: () => [{ type: 'Gifting', id: 'LIST' }],
    }),

    updateGiftCard: build.mutation<
      GiftSettingsType,
      {
        orgId: string
        data: GiftSettingsType
      }
    >({
      query: ({ orgId, data }) => ({
        url: `/organisations/${orgId}/gifting/schemes/${data.id}`,
        method: 'put',
        data: { ...data, quantities: data.quantities ?? undefined },
      }),
      transformErrorResponse() {
        toast.error('Gift card could not be updated')
      },
      invalidatesTags: (result: any) => [
        { type: 'Gifting', id: result?.id },
        { type: 'Gifting', id: 'LIST' },
      ],
    }),
    getGiftActivations: build.query<
      {
        data: GiftCardInstanceType[]
        meta: {
          page: number
          totalPages: number
          total: number
        }
      },
      {
        orgId: string
        query: {
          page: number
          limit: number
          term: string
          status: GiftCardInstanceType['status'] | null
          gift_card_settings_id?: string | null
        }
      }
    >({
      query: ({ orgId, query }) => ({
        url: buildUrl(`/organisations/${orgId}/gifting/search`, query),
        method: 'get',
      }),
      transformResponse(data: any) {
        return (
          data ?? {
            data: [],
            meta: { page: 1, totalPages: 1, total: 0 },
          }
        )
      },
      providesTags: (results: any) => [
        ...(results?.data ?? []).map((item: any) => ({
          type: 'GiftCardsActivations' as const,
          id: item?.id,
        })),
        { type: 'GiftCardsActivations', id: 'LIST' },
      ],
    }),

    getGiftReports: build.query<
      GiftReportResponse,
      {
        orgId: string
        start: Date
        end: Date
      }
    >({
      query: ({ orgId, start, end }) => ({
        url: buildUrl(`/organisations/${orgId}/gifting/reporting`, {
          start: secondsEpoch(start.getTime()),
          end: secondsEpoch(end.getTime()),
        }),
        method: 'get',
      }),
    }),
    createGiftActivation: build.mutation<
      GiftCardInstanceType,
      {
        orgId: string
        cardId: string
        data: GiftActivationMutation
      }
    >({
      query: ({ cardId, orgId, data }) => ({
        url: `/organisations/${orgId}/gifting/cards/${cardId}`,
        method: 'post',
        data,
      }),
      transformErrorResponse(response: any) {
        const message =
          response?.data?.message ??
          response?.data?.error ??
          'Gift card cannot be created'
        toast.error(message)
        return response
      },
      invalidatesTags: () => [{ type: 'GiftCardsActivations', id: 'LIST' }],
    }),

    createGiftRefund: build.mutation<
      GiftCardInstanceType,
      {
        gift: GiftCardInstanceType
        orgId: string
      }
    >({
      query: ({ gift, orgId }) => ({
        url: `/organisations/${orgId}/gifting/cards/${gift.id}/refund`,
        method: 'post',
      }),
      transformResponse(data: GiftCardInstanceType) {
        toast.success('Gift card refunded')
        return data
      },
      transformErrorResponse() {
        toast.error('Gift card cannot be refunded')
      },
      invalidatesTags: (item: any) => [
        { type: 'GiftCardsActivations', id: item?.id },
      ],
    }),
    redeemUserGiftCard: build.mutation<
      GiftCardInstanceType,
      {
        id: string
        orgId: string
      }
    >({
      query: ({ id, orgId }) => ({
        url: `/organisations/${orgId}/gifting/cards/${id}/redeem`,
        method: 'post',
      }),
      transformResponse(data: GiftCardInstanceType) {
        toast.success('Gift card redeemed')
        return data
      },
      transformErrorResponse(error) {
        if (isAxiosError(error) && error?.response?.status === 403) {
          toast.error('This gift card has already been redeemed')
          return
        }
        toast.error('Gift card cannot be redeemed')
      },
      invalidatesTags: (item: any) => [
        { type: 'GiftCardsActivations', id: item?.id },
        { type: 'GiftCardsActivations', id: 'LIST' },
      ],
    }),
    deactivateUserGiftCard: build.mutation<
      GiftCardInstanceType,
      {
        id: string
        orgId: string
      }
    >({
      query: ({ id, orgId }) => ({
        url: `/organisations/${orgId}/gifting/cards/${id}/deactivate`,
        method: 'post',
      }),
      transformResponse(data: GiftCardInstanceType) {
        toast.success('Gift card deactivated')
        return data
      },
      transformErrorResponse(error) {
        if (isAxiosError(error) && error?.response?.status === 400) {
          toast.error('This gift card cannot be deactivated')
          return
        }
        toast.error('Gift card cannot be deactivated')
      },
      invalidatesTags: (item: any) => [
        { type: 'GiftCardsActivations', id: item?.id },
        { type: 'GiftCardsActivations', id: 'LIST' },
      ],
    }),
    changeGiftOwner: build.mutation<
      GiftCardInstanceType,
      {
        orgId: string
        gift: GiftCardInstanceType
        profile: CandidateProfileType
      }
    >({
      query: ({ gift, profile: data, orgId }) => ({
        url: `/organisations/${orgId}/gifting/cards/${gift.id}/owner`,
        method: 'put',
        data,
      }),
      transformResponse(data: GiftCardInstanceType) {
        toast.success('Gift card owner updated successfully')
        return data
      },
      transformErrorResponse() {
        toast.error('Could not change owner of card')
      },
      invalidatesTags: (item: any) => [
        { type: 'GiftCardsActivations', id: item?.id },
      ],
    }),

    /**
     * Gifting end
     */

    getTempOrgReg: build.query<
      { org_reg_id: string },
      {
        orgId: string
        core_id: string
      }
    >({
      query: ({ orgId, core_id }) => ({
        url: `/organisations/${orgId}/temporary/org-reg?core_id=${core_id}`,
        method: 'get',
      }),
    }),

    /**
     *  Admin
     */

    getOrgRegCounts: build.query<
      {
        data: {
          id: string
          organization_id: string
          segment_id: string | null | undefined
          data_count: null
          email_count: null
          sms_count: null
          subscription_active: boolean
          subscription_allowance: number
          subscription_allowance_used: string
          created_at: string
        }[]
        next_cursor: string | null | undefined
      },
      { allowance_gte: number; cursor: string }
    >({
      query: ({ allowance_gte, cursor }) => ({
        url: `/admin/organisation-registration-counts?allowance_gte=${allowance_gte}&cursor=${cursor}`,
        method: 'get',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        return JSON.stringify({
          allowance_gte: queryArgs.allowance_gte,
          cursor: '',
        })
      },
      merge: (currentCache, newItems) => {
        currentCache.next_cursor = newItems.next_cursor

        newItems.data.forEach((item: any) => {
          const current = currentCache.data.findIndex(
            (it: any) => it?.id === item?.id
          )
          if (current >= 0) {
            currentCache.data[current] = item
          } else {
            currentCache.data.push(item)
          }
        })
      },

      forceRefetch({ currentArg, previousArg }) {
        return (
          !isEqual(currentArg?.cursor, previousArg?.cursor) ||
          !isEqual(currentArg?.allowance_gte, previousArg?.allowance_gte)
        )
      },
    }),
    getRecursiveRegCounts: build.query<
      {
        data: {
          id: string
          organization_id: string
          segment_id: string | null | undefined
          data_count: null
          email_count: null
          sms_count: null
          subscription_active: boolean
          subscription_allowance: number
          subscription_allowance_used: string
          created_at: string
        }[]
        next_cursor: string | null | undefined
      },
      { allowance_gte: number; cursor: string }
    >({
      query: ({ allowance_gte, cursor }) => ({
        url: `/admin/organisation-registration-recursive-counts?allowance_gte=${allowance_gte}&cursor=${cursor}`,
        method: 'get',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        return JSON.stringify({
          allowance_gte: queryArgs.allowance_gte,
          cursor: '',
        })
      },
      merge: (currentCache, newItems) => {
        currentCache.next_cursor = newItems.next_cursor

        newItems.data.forEach((item: any) => {
          const current = currentCache.data.findIndex(
            (it: any) => it?.id === item?.id
          )
          if (current >= 0) {
            currentCache.data[current] = item
          } else {
            currentCache.data.push(item)
          }
        })
      },

      forceRefetch({ currentArg, previousArg }) {
        return (
          !isEqual(currentArg?.cursor, previousArg?.cursor) ||
          !isEqual(currentArg?.allowance_gte, previousArg?.allowance_gte)
        )
      },
    }),
    getSingleInteraction: build.query<
      InteractionType,
      {
        orgId: string
        id: string
      }
    >({
      query: ({ orgId, id }) => ({
        url: `/organisations/${orgId}/interactions/${id}`,
        method: 'get',
      }),
    }),
    getSingleInteractionProfile: build.query<
      ServiceCursorPaginatedResponseType<
        Omit<InteractionProfileType, 'interaction'>
      >,
      { orgId: string; id: string; cursor: string }
    >({
      query: ({ orgId, id, cursor }) => ({
        url: `/organisations/${orgId}/interactions/${id}/profiles?cursor=${cursor}`,
        method: 'get',
      }),
    }),
    getSingleInteractionProfilesCount: build.query<
      { total: number },
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        url: `/organisations/${orgId}/interactions/${id}/total-count`,
        method: 'get',
      }),
    }),

    getOptInCounts: build.query<
      {
        data: number
        email: number
        sms: number
        marketable: number
      },
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: `/organisations/${orgId}/crm/customers/opt-in-counts`,
        method: 'get',
      }),
    }),

    /**
     *  Public
     */

    getOrgParents: build.query<
      { parents: { id: string; name: string }[] },
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: `/public/organisations/${orgId}/parents`,
        method: 'get',
      }),
    }),

    /**
     *  Public end
     */

    /**
     * Knowledge Base
     */

    // Guides
    getKnowledgeGuides: build.query<
      KnowledgeBasePaginatedResponse<KnowledgeGuide>,
      {
        limit?: number
        cursor?: string
        noCache?: boolean
        tags?: string[]
        categories?: string[]
        author?: string
      }
    >({
      query: ({ limit = 15, cursor, noCache, tags, categories, author }) => ({
        url: buildUrl('/knowledge-base', {
          limit,
          cursor,
          ...(tags?.length ? { tags: tags.join(',') } : {}),
          ...(categories?.length ? { categories: categories.join(',') } : {}),
          ...(author ? { author } : {}),
        }),
        method: 'GET',
        headers: noCache ? { 'Cache-Control': 'no-cache' } : undefined,
      }),
      providesTags: (result: any) => [
        { type: 'KnowledgeBase', id: 'LIST' },
        ...(result?.data ?? []).map((guide: any) => ({
          type: 'KnowledgeBase' as const,
          id: guide.id,
        })),
      ],
    }),

    createKnowledgeGuide: build.mutation<
      KnowledgeGuide,
      {
        title: string
        visibility: 'PUBLIC' | 'PRIVATE'
        tags?: string[]
        categories?: string[]
      }
    >({
      query: (data: any) => ({
        url: '/knowledge-base',
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'KnowledgeBase', id: 'LIST' }],
    }),

    getKnowledgeGuide: build.query<
      KnowledgeGuide,
      { id: string; noCache?: boolean }
    >({
      query: ({ id, noCache }) => ({
        url: `/knowledge-base/${id}`,
        method: 'GET',
        headers: noCache ? { 'Cache-Control': 'no-cache' } : undefined,
      }),
      providesTags: (result: any) =>
        result ? [{ type: 'KnowledgeBase', id: result.id }] : [],
    }),

    updateKnowledgeGuide: build.mutation<
      KnowledgeGuide,
      {
        id: string
        data: Partial<{
          title: string
          visibility: 'PUBLIC' | 'PRIVATE'
          tags: string[]
          categories: string[]
        }>
      }
    >({
      query: ({ id, data }) => ({
        url: `/knowledge-base/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result: any) => [
        { type: 'KnowledgeBase', id: result?.id },
        { type: 'KnowledgeBase', id: 'LIST' },
      ],
    }),

    deleteKnowledgeGuide: build.mutation<void, string>({
      query: (id: any) => ({
        url: `/knowledge-base/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'KnowledgeBase', id: 'LIST' }],
    }),

    // Blocks
    getKnowledgeBlocks: build.query<
      KnowledgeBlock[],
      { guideId: string; noCache?: boolean }
    >({
      query: ({ guideId, noCache }) => ({
        url: `/knowledge-base/${guideId}/blocks`,
        method: 'GET',
        headers: noCache ? { 'Cache-Control': 'no-cache' } : undefined,
      }),
      providesTags: (result, _error, { guideId }) => [
        { type: 'KnowledgeBase', id: `blocks-${guideId}` },
      ],
    }),

    createKnowledgeBlock: build.mutation<
      KnowledgeBlock,
      {
        guideId: string
        data: { title: string; description?: string; order: number }
      }
    >({
      query: ({ guideId, data }) => ({
        url: `/knowledge-base/${guideId}/blocks`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_result, _error, { guideId }) => [
        { type: 'KnowledgeBase', id: `blocks-${guideId}` },
        { type: 'KnowledgeBase', id: guideId },
      ],
    }),

    updateKnowledgeBlock: build.mutation<
      KnowledgeBlock,
      {
        guideId: string
        blockId: string
        data: Partial<{
          title: string
          description: string | null | undefined
          order: number
        }>
      }
    >({
      query: ({ guideId, blockId, data }) => ({
        url: `/knowledge-base/${guideId}/blocks/${blockId}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_result, _error, { guideId }) => [
        { type: 'KnowledgeBase', id: `blocks-${guideId}` },
        { type: 'KnowledgeBase', id: guideId },
      ],
    }),

    deleteKnowledgeBlock: build.mutation<
      void,
      { guideId: string; blockId: string }
    >({
      query: ({ guideId, blockId }) => ({
        url: `/knowledge-base/${guideId}/blocks/${blockId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { guideId }) => [
        { type: 'KnowledgeBase', id: `blocks-${guideId}` },
        { type: 'KnowledgeBase', id: guideId },
      ],
    }),

    // Images
    getKnowledgeImages: build.query<
      KnowledgeImage[],
      { guideId: string; blockId: string; noCache?: boolean }
    >({
      query: ({ guideId, blockId, noCache }) => ({
        url: `/knowledge-base/${guideId}/blocks/${blockId}/images`,
        method: 'GET',
        headers: noCache ? { 'Cache-Control': 'no-cache' } : undefined,
      }),
      providesTags: (_result, _error, { blockId }) => [
        { type: 'KnowledgeBase', id: `images-${blockId}` },
      ],
    }),

    createKnowledgeImage: build.mutation<
      KnowledgeImage,
      {
        guideId: string
        blockId: string
        data: { url: string; alt?: string; order: number }
      }
    >({
      query: ({ guideId, blockId, data }) => ({
        url: `/knowledge-base/${guideId}/blocks/${blockId}/images`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (_result, _error, { blockId, guideId }) => [
        { type: 'KnowledgeBase', id: `images-${blockId}` },
        { type: 'KnowledgeBase', id: guideId },
      ],
    }),

    updateKnowledgeImage: build.mutation<
      KnowledgeImage,
      {
        guideId: string
        blockId: string
        imageId: string
        data: Partial<{
          url: string
          alt: string | null | undefined
          order: number
        }>
      }
    >({
      query: ({ guideId, blockId, imageId, data }) => ({
        url: `/knowledge-base/${guideId}/blocks/${blockId}/images/${imageId}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_result, _error, { blockId, guideId }) => [
        { type: 'KnowledgeBase', id: `images-${blockId}` },
        { type: 'KnowledgeBase', id: guideId },
      ],
    }),

    deleteKnowledgeImage: build.mutation<
      void,
      { guideId: string; blockId: string; imageId: string }
    >({
      query: ({ guideId, blockId, imageId }) => ({
        url: `/knowledge-base/${guideId}/blocks/${blockId}/images/${imageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { blockId, guideId }) => [
        { type: 'KnowledgeBase', id: `images-${blockId}` },
        { type: 'KnowledgeBase', id: guideId },
      ],
    }),

    /**
     * Knowledge Base end
     */

    /**
     * Admin - User Management
     */
    disableUserAccount: build.mutation<
      {
        message: string
        disabled_user_id: string
        new_owner_id: string
      },
      {
        oauth_user_id: string
        new_owner_oauth_user_id: string
      }
    >({
      query: (data) => ({
        url: `/admin/oauth-users/disable-account`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),

    adminDisableTwoFactor: build.mutation<{ message: string }, { id: string }>({
      query: ({ id }) => ({
        url: `/admin/oauth-users/${id}/disable-two-factor`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),
    /**
     * Admin - User Management end
     */

    /**
     * Email Templates AI
     */
    generateAiEmailTemplates: build.mutation<
      {
        success: boolean
        message: string
        data: {
          name: string
          website_url: string
          organisation_id: string
          estimated_time: string
          templates_generating: string[]
        }
      },
      {
        orgId: string
        data: {
          name: string
          website_url: string
        }
      }
    >({
      query: ({ orgId, data }) => ({
        url: `/organisations/${orgId}/marketing/email/email-templates-ai`,
        method: 'POST',
        data,
      }),
    }),
    /**
     * Email Templates AI end
     */

    /**
     * Venues Summary - consolidated endpoint for venue cards
     */
    getVenuesSummary: build.query<VenuesSummaryResponse, { orgId: string }>({
      query: ({ orgId }) => ({
        url: `/organisations/${orgId}/venues/summary`,
        method: 'get',
      }),
      providesTags: ['VenuesSummary'],
    }),

    /**
     * Transfer organization ownership to a different user.
     */
    changeOrganizationOwner: build.mutation<
      { message: string },
      { orgId: string; new_owner_id: string }
    >({
      query: ({ orgId, new_owner_id }) => ({
        url: `/admin/organisations/${orgId}/change-owner`,
        method: 'POST',
        data: { new_owner_id },
      }),
    }),

    /**
     * Move an organisation under a different parent (admin endpoint).
     */
    reparentOrganisation: build.mutation<
      {
        message: string
        organisation_id: string
        old_parent_id: string | null
        new_parent_id: string
      },
      { orgId: string; new_parent_id: string }
    >({
      query: ({ orgId, new_parent_id }) => ({
        url: `/admin/organisations/${orgId}/parent`,
        method: 'PUT',
        data: { new_parent_id },
      }),
      invalidatesTags: (result, error, { orgId, new_parent_id }) => [
        { type: 'Organisation', id: orgId },
        { type: 'Organisation', id: new_parent_id },
        ...(result?.old_parent_id
          ? [{ type: 'Organisation' as const, id: result.old_parent_id }]
          : []),
        { type: 'Organisation', id: 'LIST' },
        { type: 'Organisation', id: 'SEARCH' },
      ],
    }),

    /**
     * Unlink a venue from its organisation (admin endpoint).
     */
    unlinkVenueFromOrganisation: build.mutation<
      { message: string },
      { serial: string }
    >({
      query: ({ serial }) => ({
        url: `/admin/venues/${serial}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Venue', id: 'LIST' }],
    }),

    /**
     * Onboarding endpoints
     */
    getOnboardingJobs: build.query<
      OnboardingJobListResponse,
      {
        page?: number
        limit?: number
        status?: string
        /** Matches the organisation, or the Stampede person running it. */
        search?: string
        /** The Stampede person in charge, by uid. */
        created_by?: string
        /** Used to find out whether an organisation already has a job. */
        organisation_id?: string
        /** Show archived drafts instead of live ones. */
        archived?: 'true' | 'false'
        sort?: 'created_at' | 'status' | 'organisation'
        direction?: 'asc' | 'desc'
      }
    >({
      query: (params) => ({
        url: buildUrl('/admin/onboarding', params),
        method: 'GET',
      }),
      providesTags: [{ type: 'OnboardingJob', id: 'LIST' }],
    }),

    getOnboardingJob: build.query<{ data: OnboardingJobDetail }, string>({
      query: (id) => ({
        url: `/admin/onboarding/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'OnboardingJob', id }],
    }),

    submitOnboardingJob: build.mutation<
      { data: OnboardingJobDetail },
      OnboardingJobPayload
    >({
      query: (data) => ({
        url: '/admin/onboarding',
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'OnboardingJob', id: 'LIST' }],
    }),

    getOnboardingOwners: build.query<
      { data: OnboardingOwner[] },
      { status?: string }
    >({
      query: (params) => ({
        url: buildUrl('/admin/onboarding/owners', params),
        method: 'GET',
      }),
      providesTags: [{ type: 'OnboardingJob', id: 'LIST' }],
    }),

    /**
     * The customer's own setup list for an organisation.
     *
     * `data: null` is the ordinary answer, not a failure: most
     * organisations were never put through an onboarding job. Both the
     * page and the nav item read this to decide whether they exist.
     */
    getCustomerOnboarding: build.query<
      { data: CustomerOnboardingView | null },
      string
    >({
      query: (org_id) => ({
        url: `/organisations/${org_id}/onboarding`,
        method: 'GET',
      }),
      providesTags: (_result, _error, org_id) => [
        { type: 'CustomerOnboarding', id: org_id },
      ],
    }),

    /**
     * "I have just done that" — re-poll now rather than wait for the
     * daily pass. Server-side there is a short cooldown, which answers
     * with the stored state; the page prints when the checks last ran,
     * so an unchanged timestamp is the honest result rather than a lie.
     */
    refreshCustomerOnboarding: build.mutation<
      { data: CustomerOnboardingView | null },
      string
    >({
      query: (org_id) => ({
        url: `/organisations/${org_id}/onboarding/checks`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, org_id) => [
        { type: 'CustomerOnboarding', id: org_id },
      ],
    }),

    /** The customer's own onboarding calls: booked, held and still open. */
    getOnboardingSessions: build.query<
      { data: OnboardingSessionsView },
      string
    >({
      query: (org_id) => ({
        url: `/organisations/${org_id}/onboarding/sessions`,
        method: 'GET',
      }),
      providesTags: (_r, _e, org_id) => [
        { type: 'CustomerOnboarding', id: org_id },
      ],
    }),

    /**
     * Times that would work for one call.
     *
     * Deliberately not cached beyond the component: a slot list even a
     * minute stale is a customer picking a time that has just gone, and
     * the booking then fails for a reason they cannot see.
     */
    getOnboardingSessionAvailability: build.query<
      { data: OnboardingAvailability },
      { orgId: string; session: string; from?: string; days?: number }
    >({
      query: ({ orgId, session, from, days }) => ({
        url: `/organisations/${orgId}/onboarding/sessions/availability`,
        method: 'GET',
        params: {
          session,
          ...(from ? { from } : {}),
          ...(days ? { days } : {}),
        },
      }),
      keepUnusedDataFor: 0,
    }),

    bookOnboardingSession: build.mutation<
      {
        data: {
          id: string | null
          start: string | null
          meetingUrl: string | null
        }
      },
      {
        orgId: string
        session: string
        start: string
        attendees?: string[]
        /** Required for an ad-hoc call: it becomes the event's title. */
        topic?: string
      }
    >({
      query: ({ orgId, ...body }) => ({
        url: `/organisations/${orgId}/onboarding/sessions/book`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_r, _e, { orgId }) => [
        { type: 'CustomerOnboarding', id: orgId },
      ],
    }),

    /** Poll now rather than waiting for the daily pass. */
    refreshOnboardingChecks: build.mutation<
      { data: SetupChecksResponse },
      string
    >({
      query: (id) => ({
        url: `/admin/onboarding/${id}/checks`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'OnboardingJob', id }],
    }),

    /**
     * Switch checks off for one job, and back on. Which checks apply is
     * derived from what the customer bought, which is the right default
     * and the wrong answer often enough to need an override. The server
     * re-runs on save, so the response is the new state, not the old one.
     */
    setOnboardingDisabledChecks: build.mutation<
      { data: SetupChecksResponse },
      { id: string; disabled_checks: string[] }
    >({
      query: ({ id, disabled_checks }) => ({
        url: `/admin/onboarding/${id}/checks`,
        method: 'PATCH',
        data: { disabled_checks },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'OnboardingJob', id },
      ],
    }),

    /**
     * Record whether this customer is on Square.
     *
     * Every other check works out whether it applies from what the
     * account bought. Square is not on the subscription, so this is the
     * only way the Square check can ever appear on a customer's setup
     * list. The server re-runs the checks on save, so the row shows up
     * (or goes away) without a second request.
     */
    setOnboardingUsesSquare: build.mutation<
      { data: SetupChecksResponse & { uses_square: boolean } },
      { id: string; uses_square: boolean }
    >({
      query: ({ id, uses_square }) => ({
        url: `/admin/onboarding/${id}/uses-square`,
        method: 'PATCH',
        data: { uses_square },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'OnboardingJob', id },
      ],
    }),

    /**
     * Take individual people off the chase for this job. Switching a
     * check off stops everyone being asked about that thing; this stops
     * one person being asked about any of it.
     */
    setOnboardingMutedRecipients: build.mutation<
      { data: ReminderRecipient[] },
      { id: string; muted_recipients: string[] }
    >({
      query: ({ id, muted_recipients }) => ({
        url: `/admin/onboarding/${id}/recipients`,
        method: 'PATCH',
        data: { muted_recipients },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'OnboardingJob', id },
      ],
    }),

    /** A soft archive: the job leaves the list, not the database. */
    archiveOnboardingJob: build.mutation<
      { data: { id: string; archived: boolean } },
      string
    >({
      query: (id) => ({ url: `/admin/onboarding/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'OnboardingJob', id: 'LIST' }],
    }),

    createOnboardingDraft: build.mutation<
      { data: OnboardingJob },
      {
        organisation_id: string
        subscription_id?: string
        /** The deal this came from, so the briefing follows the job. */
        hubspot_deal_id?: string
      }
    >({
      query: (data) => ({
        url: '/admin/onboarding/draft',
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'OnboardingJob', id: 'LIST' }],
    }),

    updateOnboardingDraft: build.mutation<
      { data: OnboardingJob },
      {
        id: string
        payload?: Record<string, unknown>
        subscription_id?: string | null
        /** Attach or clear the HubSpot deal, including after a job ran. */
        hubspot_deal_id?: string | null
      }
    >({
      query: ({ id, ...data }) => ({
        url: `/admin/onboarding/${id}`,
        method: 'PATCH',
        data,
      }),
    }),

    getOnboardingCalendarEvents: build.query<
      { data: OnboardingCalendarEvent[] },
      { timeMin: string; timeMax: string }
    >({
      query: (params) => ({
        url: buildUrl('/admin/onboarding/calendar/events', params),
        method: 'GET',
      }),
    }),

    discoverOnboardingVenues: build.mutation<
      { data: DiscoveredVenue[] },
      { website_url: string }
    >({
      query: (data) => ({
        url: '/admin/onboarding/discover-venues',
        method: 'POST',
        data,
      }),
    }),

    getOnboardingBrandKitPreview: build.query<
      { data: OnboardingBrandKitPreview },
      { website_url: string; logo_url?: string }
    >({
      query: (params) => ({
        url: buildUrl('/admin/onboarding/preview/brand-kit', params),
        method: 'GET',
      }),
    }),

    previewOnboardingEmail: build.mutation<
      { data: OnboardingEmailPreview[] },
      {
        organisation_id: string
        website_url: string
        name: string
        logo_url?: string
      }
    >({
      query: (data) => ({
        url: '/admin/onboarding/preview/email',
        method: 'POST',
        data,
      }),
    }),

    getOnboardingSubscription: build.query<
      { data: OnboardingSubscriptionResponse },
      string
    >({
      query: (orgId) => ({
        url: `/admin/onboarding/subscription/${orgId}`,
        method: 'GET',
      }),
    }),

    /**
     * Teams endpoints
     */
    getOrganisationTeams: build.query<OrgTeam[], string>({
      query: (orgId) => ({
        url: `/organisations/${orgId}/users/teams`,
        method: 'GET',
      }),
      providesTags: [{ type: 'OrgTeam' as const, id: 'LIST' }],
    }),
    getOrganisationTeam: build.query<
      OrgTeam,
      { orgId: string; teamId: string }
    >({
      query: ({ orgId, teamId }) => ({
        url: `/organisations/${orgId}/users/teams/${teamId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { teamId }) => [
        { type: 'OrgTeam' as const, id: teamId },
      ],
    }),
    createOrganisationTeam: build.mutation<
      OrgTeam,
      {
        orgId: string
        data: { name: string; owner_id?: string; member_ids?: string[] }
      }
    >({
      query: ({ orgId, data }) => ({
        url: `/organisations/${orgId}/users/teams`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'OrgTeam' as const, id: 'LIST' }],
    }),
    updateOrganisationTeam: build.mutation<
      OrgTeam,
      {
        orgId: string
        teamId: string
        data: { name?: string; owner_id?: string }
      }
    >({
      query: ({ orgId, teamId, data }) => ({
        url: `/organisations/${orgId}/users/teams/${teamId}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_result, _error, { teamId }) => [
        { type: 'OrgTeam' as const, id: teamId },
        { type: 'OrgTeam' as const, id: 'LIST' },
      ],
    }),
    deleteOrganisationTeam: build.mutation<
      { message: string },
      { orgId: string; teamId: string }
    >({
      query: ({ orgId, teamId }) => ({
        url: `/organisations/${orgId}/users/teams/${teamId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'OrgTeam' as const, id: 'LIST' }],
    }),
    addTeamMember: build.mutation<
      OrgTeamMember,
      { orgId: string; teamId: string; user_id: string }
    >({
      query: ({ orgId, teamId, user_id }) => ({
        url: `/organisations/${orgId}/users/teams/${teamId}/members`,
        method: 'POST',
        data: { user_id },
      }),
      invalidatesTags: (_result, _error, { teamId }) => [
        { type: 'OrgTeam' as const, id: teamId },
        { type: 'OrgTeam' as const, id: 'LIST' },
      ],
    }),
    removeTeamMember: build.mutation<
      { message: string },
      { orgId: string; teamId: string; userId: string }
    >({
      query: ({ orgId, teamId, userId }) => ({
        url: `/organisations/${orgId}/users/teams/${teamId}/members/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { teamId }) => [
        { type: 'OrgTeam' as const, id: teamId },
        { type: 'OrgTeam' as const, id: 'LIST' },
      ],
    }),

    /**
     * Update stampede owner on an organisation
     */
    updateStampedeOwner: build.mutation<
      OrganisationType,
      { orgId: string; stampede_owner_id: string | null }
    >({
      query: ({ orgId, stampede_owner_id }) => ({
        url: `/organisations/${orgId}`,
        method: 'PUT',
        data: { stampede_owner_id },
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'Organisation', id: orgId },
      ],
    }),

    setupTwoFactor: build.mutation<
      { provisioning_uri: string; qr_code: string },
      Record<string, never>
    >({
      query: () => ({
        url: '/oauth/two-factor/setup',
        method: 'POST',
      }),
    }),

    enableTwoFactor: build.mutation<{ enabled_at: string }, { code: string }>({
      query: (input) => ({
        url: '/oauth/two-factor/enable',
        method: 'POST',
        data: { otp: input.code },
      }),
    }),

    disableTwoFactor: build.mutation<Record<string, never>, { code: string }>({
      query: (input) => ({
        url: '/oauth/two-factor',
        method: 'DELETE',
        data: { otp: input.code },
      }),
    }),

    resendEmailVerification: build.mutation<{ message: string }, void>({
      query: () => ({
        url: '/oauth/email-verification/resend',
        method: 'POST',
      }),
    }),

    verifyEmail: build.mutation<{ verified_at: string }, { code: string }>({
      query: (input) => ({
        url: '/oauth/email-verification/verify',
        method: 'POST',
        data: { code: input.code },
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),

    getUserInfo: build.query<OAuthUserInfo, Record<string, never>>({
      query: () => ({
        url: '/oauth/userinfo',
        method: 'GET',
      }),
    }),

    generateSupportVerificationCode: build.mutation<
      SupportVerificationCode,
      void
    >({
      query: () => ({
        url: '/oauth/support-verification-code',
        method: 'POST',
      }),
    }),

    logout: build.mutation({
      query: () => ({
        url: '/oauth/logout',
        method: 'post',
      }),
      transformResponse() {
        localStorage.setItem(config.localStorageKeys.userCredentials, '')
        return null
      },
      transformErrorResponse() {
        localStorage.setItem(config.localStorageKeys.userCredentials, '')
        return null
      },
    }),

    /**
     * Gallery endpoints
     */
    getMembersGallery: build.query<
      GalleryGetResponse,
      {
        orgId: string
        offset?: number
        kind?: string
        path?: string
        label_like?: string
        name_like?: string
      }
    >({
      query: ({ orgId, offset, kind, path, label_like, name_like }) => ({
        url: buildUrl(`/organisations/${orgId}/gallery`, {
          offset,
          kind,
          path,
          limit: 50,
          label_like,
          name_like,
        }),
        method: 'get',
      }),
      serializeQueryArgs: ({ queryArgs }) =>
        JSON.stringify({ ...queryArgs, offset: '' }),
      merge: (currentCache, newItems, { arg }) => {
        if (arg.offset) {
          currentCache.body.push(...newItems.body)
          currentCache.has_more = newItems.has_more
          currentCache.next_offset = newItems.next_offset
          currentCache.total = newItems.total
        } else {
          Object.assign(currentCache, newItems)
        }
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.offset !== previousArg?.offset,
      providesTags: (result) =>
        result
          ? [
              ...result.body.map((item) => ({
                type: 'Gallery' as const,
                id: item.id,
              })),
              { type: 'Gallery', id: 'LIST' },
            ]
          : [{ type: 'Gallery', id: 'LIST' }],
    }),

    deleteGalleryItem: build.mutation<
      { id: string },
      { orgId: string; id: number }
    >({
      query: ({ orgId, id }) => ({
        url: `/organisations/${orgId}/gallery/${id}`,
        method: 'delete',
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Gallery', id }],
    }),

    getOrganisationPersonalisationCards: build.query<
      OrganisationPersonalisationCardType[],
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: `/organisations/${orgId}/personalisation/cards`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((card) => ({
                type: 'PersonalisationCards' as const,
                id: card.id,
              })),
              { type: 'PersonalisationCards' as const, id: 'LIST' },
            ]
          : [{ type: 'PersonalisationCards' as const, id: 'LIST' }],
    }),

    updateOrganisationPersonalisationCard: build.mutation<
      OrganisationPersonalisationCardType,
      { card: OrganisationPersonalisationCardType }
    >({
      query: ({ card }) => ({
        url: `/organisations/${card.organization_id}/personalisation/cards`,
        method: 'PUT',
        data: card,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'PersonalisationCards' as const, id: result.id },
              { type: 'PersonalisationCards' as const, id: 'LIST' },
            ]
          : [{ type: 'PersonalisationCards' as const, id: 'LIST' }],
    }),

    createOrganisationPersonalisationCard: build.mutation<
      OrganisationPersonalisationCardType,
      { card: Partial<OrganisationPersonalisationCardType> }
    >({
      query: ({ card }) => ({
        url: `/organisations/${card.organization_id}/personalisation/cards`,
        method: 'POST',
        data: card,
      }),
      invalidatesTags: [{ type: 'PersonalisationCards' as const, id: 'LIST' }],
    }),

    /**
     * Personalisation endpoints
     */
    getVenuePersonalisationCards: build.query<
      LocationPersonalisationCardsType[],
      { orgId: string; serial: string }
    >({
      query: ({ orgId, serial }) => ({
        url: `/organisations/${orgId}/personalisation/cards/venue/${serial}`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((card) => ({
                type: 'PersonalisationCards' as const,
                id: `venue-${card.id}`,
              })),
              {
                type: 'PersonalisationCards' as const,
                id: `venue-LIST-${result[0]?.serial}`,
              },
            ]
          : [{ type: 'PersonalisationCards' as const, id: 'venue-LIST' }],
    }),

    addVenuePersonalisationCard: build.mutation<
      LocationPersonalisationCardsType,
      {
        orgId: string
        serial: string
        card: OrganisationPersonalisationCardType
        order_by: number
      }
    >({
      query: ({ orgId, serial, card, order_by }) => ({
        url: `/organisations/${orgId}/personalisation/cards/venue/${serial}`,
        method: 'POST',
        data: { card_id: card.id, order_by },
      }),
      invalidatesTags: (_, __, { serial }) => [
        { type: 'PersonalisationCards' as const, id: `venue-LIST-${serial}` },
        { type: 'PersonalisationCards' as const, id: 'venue-LIST' },
      ],
    }),

    removeVenuePersonalisationCard: build.mutation<
      { serial: string; card_id: string },
      { orgId: string; serial: string; card_id: string }
    >({
      query: ({ orgId, serial, card_id }) => ({
        url: `/organisations/${orgId}/personalisation/cards/venue/${serial}?card_id=${card_id}`,
        method: 'DELETE',
      }),
      transformResponse: (_, __, { serial, card_id }) => ({ serial, card_id }),
      invalidatesTags: (_, __, { serial }) => [
        { type: 'PersonalisationCards' as const, id: `venue-LIST-${serial}` },
        { type: 'PersonalisationCards' as const, id: 'venue-LIST' },
      ],
    }),

    orderVenuePersonalisationCards: build.mutation<
      LocationPersonalisationCardsType[],
      {
        orgId: string
        serial: string
        cards: LocationPersonalisationCardsType[]
      }
    >({
      query: ({ orgId, serial, cards }) => ({
        url: `/organisations/${orgId}/personalisation/cards/venue/${serial}`,
        method: 'PUT',
        data: cards,
      }),
      invalidatesTags: (_, __, { serial }) => [
        { type: 'PersonalisationCards' as const, id: `venue-LIST-${serial}` },
      ],
    }),
  }),
})

export interface VenuesSummaryResponse {
  organisation_id: string
  generated_at: string

  totals: {
    venue_count: number
    new_customers_30d: number
    returning_customers_30d: number
    avg_opt_in_rate: number
  }

  venues: VenueSummary[]
}

export interface VenueSummary {
  serial: string

  customer_stats: {
    new_customer_count: number
    returning_customer_count: number
    total_customers_count: number
    marketing_opt_in_count: number
    opt_in_rate: number
    daily_visits: {
      date: string
      visits: number
    }[]
  }

  reviews: {
    total_reviews: number
    avg_rating: number
  }

  recent_customers: {
    id: string
    first_name: string | null
    last_name: string | null
    email: string | null
  }[]

  last_order: string | null

  wifi_status: string | null

  services: {
    bookings_enabled: boolean
    loyalty_enabled: boolean
    payos_enabled: boolean
    workflow_count: number
  }
}

export default morpheusApi
